import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { optimizedImageUrl } from '../utils/imageUrl';

/**
 * Fullscreen lightbox with pan/zoom for product galleries.
 * Props:
 *  - images: string[] (URLs)
 *  - startIndex: number
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - alt: string
 */
const ImageZoomLightbox = ({ images = [], startIndex = 0, isOpen, onClose, alt = 'Producto' }) => {
  const [currentIdx, setCurrentIdx] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  const resetZoom = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    if (isOpen) setCurrentIdx(startIndex);
  }, [isOpen, startIndex]);

  useEffect(() => {
    resetZoom();
  }, [currentIdx, resetZoom]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === '+' || e.key === '=') zoomIn();
      else if (e.key === '-') zoomOut();
      else if (e.key === '0') resetZoom();
    };
    window.addEventListener('keydown', handleKey);
    // Prevent body scroll while open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, currentIdx, images.length]);

  const goPrev = () => {
    if (images.length <= 1) return;
    setCurrentIdx((idx) => (idx - 1 + images.length) % images.length);
  };
  const goNext = () => {
    if (images.length <= 1) return;
    setCurrentIdx((idx) => (idx + 1) % images.length);
  };

  const zoomIn = () => setScale((s) => Math.min(s + 0.5, 5));
  const zoomOut = () => setScale((s) => {
    const newScale = Math.max(s - 0.5, 1);
    if (newScale === 1) setPosition({ x: 0, y: 0 });
    return newScale;
  });

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) zoomIn();
    else zoomOut();
  };

  const handleImageClick = (e) => {
    e.stopPropagation();
    if (scale === 1) {
      setScale(2);
    } else {
      resetZoom();
    }
  };

  const handleMouseDown = (e) => {
    if (scale === 1) return;
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      posX: position.x,
      posY: position.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: dragStart.current.posX + (e.clientX - dragStart.current.x),
      y: dragStart.current.posY + (e.clientY - dragStart.current.y),
    });
  };

  const handleMouseUp = () => setIsDragging(false);

  // Touch support for mobile
  const touchData = useRef({ x: 0, y: 0, posX: 0, posY: 0, lastDist: 0 });

  const handleTouchStart = (e) => {
    if (e.touches.length === 1 && scale > 1) {
      touchData.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        posX: position.x,
        posY: position.y,
        lastDist: 0,
      };
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      touchData.current.lastDist = Math.hypot(dx, dy);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1 && scale > 1) {
      e.preventDefault();
      setPosition({
        x: touchData.current.posX + (e.touches[0].clientX - touchData.current.x),
        y: touchData.current.posY + (e.touches[0].clientY - touchData.current.y),
      });
    } else if (e.touches.length === 2) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      if (touchData.current.lastDist > 0) {
        const ratio = dist / touchData.current.lastDist;
        setScale((s) => Math.min(Math.max(s * ratio, 1), 5));
      }
      touchData.current.lastDist = dist;
    }
  };

  if (!isOpen || images.length === 0) return null;

  const currentUrl = optimizedImageUrl(images[currentIdx], 1600);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center select-none"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      data-testid="zoom-lightbox"
    >
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-20 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition-colors"
        aria-label="Cerrar"
        data-testid="zoom-close"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Counter */}
      {images.length > 1 && (
        <div className="absolute top-4 left-4 z-20 bg-white/10 text-white px-3 py-1.5 rounded-full text-sm">
          {currentIdx + 1} / {images.length}
        </div>
      )}

      {/* Zoom controls */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-2 py-1.5">
        <button
          type="button"
          onClick={zoomOut}
          disabled={scale <= 1}
          className="text-white p-1.5 hover:bg-white/10 rounded-full disabled:opacity-40"
          aria-label="Alejar"
          data-testid="zoom-out-btn"
        >
          <ZoomOut className="w-5 h-5" />
        </button>
        <span className="text-white text-sm font-mono w-12 text-center">{Math.round(scale * 100)}%</span>
        <button
          type="button"
          onClick={zoomIn}
          disabled={scale >= 5}
          className="text-white p-1.5 hover:bg-white/10 rounded-full disabled:opacity-40"
          aria-label="Acercar"
          data-testid="zoom-in-btn"
        >
          <ZoomIn className="w-5 h-5" />
        </button>
        <div className="w-px h-5 bg-white/30 mx-1" />
        <button
          type="button"
          onClick={resetZoom}
          className="text-white p-1.5 hover:bg-white/10 rounded-full"
          aria-label="Restablecer"
          data-testid="zoom-reset-btn"
        >
          <Maximize2 className="w-5 h-5" />
        </button>
      </div>

      {/* Prev */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors"
          aria-label="Anterior"
          data-testid="zoom-prev"
        >
          <ChevronLeft className="w-7 h-7" />
        </button>
      )}

      {/* Image */}
      <div
        className="w-full h-full flex items-center justify-center overflow-hidden"
        onWheel={handleWheel}
        onClick={onClose}
      >
        <img
          src={currentUrl}
          alt={alt}
          className="max-w-[90vw] max-h-[85vh] object-contain transition-transform duration-100"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            cursor: scale === 1 ? 'zoom-in' : (isDragging ? 'grabbing' : 'grab'),
          }}
          onClick={handleImageClick}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          draggable={false}
          data-testid="zoom-image"
        />
      </div>

      {/* Next */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors"
          aria-label="Siguiente"
          data-testid="zoom-next"
        >
          <ChevronRight className="w-7 h-7" />
        </button>
      )}

      {/* Hint */}
      <div className="absolute bottom-16 left-1/2 -translate-x-1/2 text-white/60 text-xs hidden sm:block">
        Clic para acercar · Rueda para zoom · Arrastra para desplazar · ESC para cerrar
      </div>
    </div>
  );
};

export default ImageZoomLightbox;
